/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import { css, jsx } from '@atlaskit/css';
import { BLOCK_CONTROL_UI_CONTEXT } from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import { BLOCK_CONTROLS_LEFT_SURFACE } from '@atlaskit/editor-common/block-controls/surface-keys';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import {
	SurfaceRenderer,
	willSurfaceRender,
} from '@atlaskit/editor-ui-control-model/surface-renderer';
import { createSurfaceContext } from '@atlaskit/editor-ui-control-model/types';
import { token } from '@atlaskit/tokens';

import type { ActiveNode, BlockControlsPlugin } from '../blockControlsPluginType';
import { getNodeTypeWithLevel } from '../pm-plugins/decorations-common';

import { createBlockControlsSurfaceContext } from './block-controls-surface-context';
import { getNodeContentElement } from './utils/get-node-content-element';
import {
	getAbsoluteSurfacePlacement,
	getSurfacePlacement,
	type SurfacePlacement,
} from './utils/get-surface-placement';
import { VisibilityContainer } from './visibility-container';

const BLOCK_CONTROLS_SURFACE_Z_INDEX = 100;

const surfaceStyles = css({
	alignItems: 'center',
	display: 'flex',
	gap: token('space.025'),
	position: 'absolute',
	zIndex: BLOCK_CONTROLS_SURFACE_Z_INDEX,
});

type Props = {
	api: ExtractInjectionAPI<BlockControlsPlugin>;
	editorView: EditorView;
};

type SurfaceAtTargetProps = Props & {
	activeNode: ActiveNode;
	target: 'active' | 'root';
};

const BlockControlsSurfaceAtTarget = ({
	activeNode,
	api,
	editorView,
	target,
}: SurfaceAtTargetProps): React.JSX.Element | null => {
	const [position, setPosition] = useState<SurfacePlacement>();
	const animationFrameRef = useRef<number | undefined>(undefined);

	const components =
		api.uiControlRegistry?.actions.getComponents(BLOCK_CONTROLS_LEFT_SURFACE) ?? [];
	const blockControlsContext = useMemo(
		() => createBlockControlsSurfaceContext(editorView, activeNode, target),
		[activeNode, editorView, target],
	);
	const surfaceContext = useMemo(
		() =>
			blockControlsContext
				? createSurfaceContext(BLOCK_CONTROL_UI_CONTEXT, blockControlsContext)
				: undefined,
		[blockControlsContext],
	);

	useLayoutEffect(() => {
		if (!blockControlsContext) {
			setPosition(undefined);
			return;
		}

		const targetNodeElement = editorView.nodeDOM(blockControlsContext.targetNode.pos);
		const targetElementRoot =
			targetNodeElement instanceof HTMLElement
				? targetNodeElement
				: targetNodeElement?.parentElement;
		if (!targetElementRoot) {
			setPosition(undefined);
			return;
		}
		const targetElement = getNodeContentElement(
			targetElementRoot,
			blockControlsContext.targetNode.type.name,
		);
		const offsetParent = editorView.dom.offsetParent;
		const isDocumentOffsetParent = !offsetParent || offsetParent === getDocument()?.body;
		const scrollingOffsetParent =
			offsetParent instanceof HTMLElement && !isDocumentOffsetParent ? offsetParent : undefined;

		const updatePosition = () => {
			animationFrameRef.current = undefined;
			if (!targetElementRoot.isConnected || !targetElement.isConnected) {
				setPosition(undefined);
				return;
			}
			const placement = getSurfacePlacement({
				layout: targetElementRoot.getAttribute('layout') ?? '',
				nodeRect: targetElement.getBoundingClientRect(),
				nodeType: blockControlsContext.targetNode.type.name,
				nodeTypeWithLevel: getNodeTypeWithLevel(blockControlsContext.targetNode.node),
				parentNodeType:
					blockControlsContext.targetNode.parentType === 'doc'
						? undefined
						: blockControlsContext.targetNode.parentType,
			});
			const offsetParentRect = offsetParent?.getBoundingClientRect();
			const offsetParentScrollTop = scrollingOffsetParent?.scrollTop ?? window.scrollY;
			const offsetParentScrollLeft = scrollingOffsetParent?.scrollLeft ?? window.scrollX;
			setPosition(
				getAbsoluteSurfacePlacement({
					offsetParentRect,
					offsetParentScrollLeft,
					offsetParentScrollTop,
					placement,
				}),
			);
		};
		const schedulePositionUpdate = () => {
			if (animationFrameRef.current === undefined) {
				animationFrameRef.current = requestAnimationFrame(updatePosition);
			}
		};

		updatePosition();
		const resizeObserver = new ResizeObserver(schedulePositionUpdate);
		resizeObserver.observe(editorView.dom);
		if (targetElement && targetElement !== editorView.dom) {
			resizeObserver.observe(targetElement);
		}
		if (offsetParent instanceof HTMLElement && offsetParent !== editorView.dom) {
			resizeObserver.observe(offsetParent);
		}
		const unbindWindowResize = bind(window, {
			type: 'resize',
			listener: schedulePositionUpdate,
		});
		const unbindWindowScroll = bind(window, {
			type: 'scroll',
			listener: schedulePositionUpdate,
			options: { capture: true, passive: true },
		});
		const visualViewport = window.visualViewport;
		const unbindVisualViewportResize = visualViewport
			? bind(visualViewport, { type: 'resize', listener: schedulePositionUpdate })
			: undefined;
		const unbindVisualViewportScroll = visualViewport
			? bind(visualViewport, { type: 'scroll', listener: schedulePositionUpdate })
			: undefined;
		const unbindTransitionEnd = bind(targetElement, {
			type: 'transitionend',
			listener: schedulePositionUpdate,
		});

		return () => {
			if (animationFrameRef.current !== undefined) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = undefined;
			}
			resizeObserver.disconnect();
			unbindWindowResize();
			unbindWindowScroll();
			unbindVisualViewportResize?.();
			unbindVisualViewportScroll?.();
			unbindTransitionEnd();
		};
	}, [blockControlsContext, editorView]);

	if (
		components.length === 0 ||
		!blockControlsContext ||
		!surfaceContext ||
		!position ||
		!willSurfaceRender(components, BLOCK_CONTROLS_LEFT_SURFACE, surfaceContext)
	) {
		return null;
	}

	return (
		<div
			css={surfaceStyles}
			data-editor-block-controls-surface
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Placement is resolved from the active node's runtime DOM geometry.
			style={position}
			data-testid={
				target === 'root' ? 'block-controls-left-surface' : 'block-controls-left-surface-active'
			}
		>
			<VisibilityContainer
				api={api}
				controlSide="left"
				forceVisibleOnMouseOut={Boolean(activeNode.handleOptions?.isFocused)}
				shouldUseDisplayContents
			>
				<SurfaceRenderer
					components={components}
					surface={BLOCK_CONTROLS_LEFT_SURFACE}
					surfaceContext={surfaceContext}
				/>
			</VisibilityContainer>
		</div>
	);
};

export const BlockControlsSurface = ({ api, editorView }: Props): React.JSX.Element | null => {
	const activeNode = useSharedPluginStateWithSelector(api, ['blockControls'], (states) => ({
		activeNode: states.blockControlsState?.activeNode,
	})).activeNode;

	if (!activeNode) {
		return null;
	}

	const rootPos = activeNode.rootPos ?? activeNode.pos;
	return (
		<>
			<BlockControlsSurfaceAtTarget
				activeNode={activeNode}
				api={api}
				editorView={editorView}
				target="root"
			/>
			{rootPos !== activeNode.pos ? (
				<BlockControlsSurfaceAtTarget
					activeNode={activeNode}
					api={api}
					editorView={editorView}
					target="active"
				/>
			) : null}
		</>
	);
};
